// client/src/components/pages/LexiStar/Dashboard/KpiTile.tsx
//
// Headline KPI tile (LS-09 §5.15; dataviz stat-tile contract): label, the
// figure, a context line, the change against the previous period, a trend
// sparkline, the provisional / final chip and the provenance mark. The whole
// tile drills through to the list that explains the figure (FR-RPT-006) via a
// stretched link, so "How is this counted?" stays a separate control.

import { Link } from 'react-router-dom';
import { formatNumber, formatValue, unitSuffix, type Better, type Unit } from './metricFormat';
import { DeltaText, ProvenanceMark, Sparkline, StateChip, Workings, type PeriodState, type SparkPoint } from './analyticsAtoms';
import type { MetricDef, MetricValue } from './kpiModel';

export interface KpiTileProps {
  label: string;
  def: MetricDef;
  value: MetricValue | undefined;
  previous: MetricValue | undefined;
  better: Better;
  trend: SparkPoint[];
  state: PeriodState | null;
  context?: string | null;
  drill: string;
}

export default function KpiTile({ label, def, value, previous, better, trend, state, context, drill }: KpiTileProps) {
  const unit: Unit = value?.unit ?? def.unit;
  const figure = formatNumber(value?.value, unit, { compactLarge: true });
  const suffix = figure === '—' ? '' : unit === '%' ? '%' : unitSuffix(unit);
  const full = formatValue(value?.value, unit);
  return (
    <article
      className="group relative flex flex-col gap-2 bg-surface rounded-xl border border-slate-200 shadow-sm p-4 transition-colors hover:border-primary-300 focus-within:border-primary-400"
      aria-label={`${label}: ${full}`}
      data-metric={def.key}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-600 leading-5" title={def.description ?? def.name}>
          {label}
        </h3>
        <StateChip state={state} className="relative z-10" />
      </div>
      <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
        <p className="leading-none whitespace-nowrap">
          <Link
            to={drill}
            className="text-[1.875rem] font-semibold tracking-tight text-slate-900 tabular-nums focus-visible:outline-none after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-primary-500"
            aria-label={`${label}: ${full} — open the list behind this figure`}
            title={full}
          >
            {figure}
            {suffix && suffix !== '%' && <span className="ml-1 text-base font-medium text-slate-500">{suffix}</span>}
            {suffix === '%' && <span className="text-xl font-medium text-slate-500">%</span>}
          </Link>
        </p>
        <Sparkline points={trend} width={96} className="flex-shrink-0 mb-0.5 ml-auto" label={`${label} trend, ${trend.length} periods`} />
      </div>
      {context && <p className="text-xs text-slate-500 tabular-nums -mt-1">{context}</p>}
      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <DeltaText current={value?.value} previous={previous?.value} unit={unit} better={better} />
        <ProvenanceMark provenance={value?.provenance ?? def.provenance_rule} className="relative z-10" />
      </div>
      <div className="relative z-10">
        <Workings
          formula={def.formula}
          description={def.description}
          provenanceRule={def.provenance_rule}
          version={def.version}
        />
      </div>
    </article>
  );
}
